import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { getStripeEnvironment } from "@/lib/stripe";
import { type AffiliateCommissionRow, maskReferredUserId } from "@/lib/affiliateDashboard";
import { downloadCsv, type PayableRow, type PayoutBatchRow, type TaxStatus } from "./types";

type ConnectStatus = { payouts_enabled: boolean };
type PayoutRunDetail =
  | { ok: true; affiliate_user_id: string; currency: string; amount_cents: number; commission_count: number; transfer_id: string }
  | { ok: false; affiliate_user_id: string; currency: string; amount_cents: number; commission_count: number; error: string };


export function usePayouts() {
  const [payable, setPayable] = useState<PayableRow[]>([]);
  const [payableLoading, setPayableLoading] = useState(true);
  const [batches, setBatches] = useState<PayoutBatchRow[]>([]);
  const [batchesLoading, setBatchesLoading] = useState(true);

  const [dialogRow, setDialogRow] = useState<PayableRow | null>(null);
  const [dialogCommissions, setDialogCommissions] = useState<AffiliateCommissionRow[]>([]);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [paymentReference, setPaymentReference] = useState("");
  const [notes, setNotes] = useState("");
  const [allowMissingTax, setAllowMissingTax] = useState(false);
  const [taxStatus, setTaxStatus] = useState<TaxStatus>(null);
  const [submitting, setSubmitting] = useState(false);
  const [connectByUser, setConnectByUser] = useState<Map<string, ConnectStatus>>(new Map());
  const [running, setRunning] = useState<string | null>(null); // "all" or `${user}-${currency}`

  const env = getStripeEnvironment();

  const loadConnect = async () => {
    const { data, error } = await supabase
      .from("affiliate_connect_accounts")
      .select("affiliate_user_id, payouts_enabled")
      .eq("environment", env);
    if (error) return;
    const m = new Map<string, ConnectStatus>();
    for (const r of data ?? []) m.set(r.affiliate_user_id, { payouts_enabled: !!r.payouts_enabled });
    setConnectByUser(m);
  };

  const loadPayable = async () => {
    setPayableLoading(true);
    const { data, error } = await supabase.rpc("get_affiliate_payable_summary");
    setPayableLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    setPayable((data ?? []) as PayableRow[]);
  };

  const loadBatches = async () => {
    setBatchesLoading(true);
    const { data, error } = await supabase
      .from("affiliate_payout_batches")
      .select(
        "id, affiliate_user_id, currency, total_amount_cents, commission_count, payment_reference, notes, paid_at, created_at",
      )
      .order("paid_at", { ascending: false })
      .limit(50);
    setBatchesLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    setBatches((data ?? []) as PayoutBatchRow[]);
  };

  useEffect(() => {
    void loadPayable();
    void loadBatches();
    void loadConnect();
    // Loaders are stable closures over `env`/setters; intentionally run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatRunResult = (details: PayoutRunDetail[]) => {
    const ok = details.filter((d) => d.ok).length;
    const fail = details.filter((d) => !d.ok).length;
    return { ok, fail };
  };

  const runPayoutOne = async (row: PayableRow) => {
    const key = `${row.affiliate_user_id}-${row.currency}`;
    setRunning(key);
    const { data, error } = await supabase.functions.invoke("affiliate-payout-run", {
      body: {
        environment: env,
        scope: "one",
        affiliate_user_id: row.affiliate_user_id,
        currency: row.currency,
      },
    });
    setRunning(null);
    if (error) {
      toast.error(error.message);
      return;
    }
    if (data?.error) {
      toast.error(data.error);
      return;
    }
    const details = (data?.result?.details ?? []) as PayoutRunDetail[];
    const { ok, fail } = formatRunResult(details);
    if (ok > 0 && fail === 0) toast.success(`Paid ${ok} group via Stripe`);
    else if (ok > 0 && fail > 0) toast.warning(`Paid ${ok}, ${fail} failed`);
    else if (fail > 0) toast.error(`Payout failed: ${(details.find((d) => !d.ok) as { error: string }).error}`);
    else toast.info(data?.message ?? "Nothing to pay");
    await Promise.all([loadPayable(), loadBatches()]);
  };

  const runPayoutAll = async () => {
    setRunning("all");
    const { data, error } = await supabase.functions.invoke("affiliate-payout-run", {
      body: { environment: env, scope: "all" },
    });
    setRunning(null);
    if (error) {
      toast.error(error.message);
      return;
    }
    if (data?.error) {
      toast.error(data.error);
      return;
    }
    const details = (data?.result?.details ?? []) as PayoutRunDetail[];
    const { ok, fail } = formatRunResult(details);
    const paid = data?.result?.paid ?? 0;
    if (ok === 0 && fail === 0) toast.info(data?.message ?? "Nothing eligible to pay right now");
    else toast.success(`Paid ${paid} commission(s) across ${ok} affiliate group(s)${fail > 0 ? ` · ${fail} failed` : ""}`);
    await Promise.all([loadPayable(), loadBatches()]);
  };


  const totalsByCurrency = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of payable) {
      map.set(r.currency, (map.get(r.currency) ?? 0) + r.payable_cents);
    }
    return [...map.entries()];
  }, [payable]);

  const exportPayableCsv = () => {
    if (payable.length === 0) {
      toast.info("Nothing to export");
      return;
    }
    const rows: string[][] = [
      ["affiliate_user_id", "currency", "payable_amount", "commission_count", "meets_minimum"],
      ...payable.map((r) => [
        r.affiliate_user_id,
        r.currency.toUpperCase(),
        (r.payable_cents / 100).toFixed(2),
        String(r.commission_count),
        r.meets_minimum ? "yes" : "no",
      ]),
    ];
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`affiliate-payable-${stamp}.csv`, rows);
    toast.success("Payable CSV exported");
  };

  const openBatchDialog = async (row: PayableRow) => {
    setDialogRow(row);
    setPaymentReference("");
    setNotes("");
    setAllowMissingTax(false);
    setTaxStatus(null);
    setDialogLoading(true);
    const [commissionsRes, taxRes] = await Promise.all([
      supabase
        .from("affiliate_commissions")
        .select(
          "id, affiliate_user_id, referred_user_id, stripe_event_id, stripe_invoice_id, stripe_subscription_id, gross_amount_cents, commission_bps, commission_amount_cents, currency, status, accrued_at, approved_at, reversed_at, forfeited_at, paid_at, reverses_commission_id, notes",
        )
        .eq("affiliate_user_id", row.affiliate_user_id)
        .eq("currency", row.currency)
        .eq("status", "approved")
        .is("payout_batch_id", null)
        .order("accrued_at", { ascending: true }),
      supabase.rpc("get_affiliate_tax_status", { _user_id: row.affiliate_user_id }),
    ]);
    setDialogLoading(false);
    if (commissionsRes.error) {
      toast.error(commissionsRes.error.message);
      setDialogRow(null);
      return;
    }
    setDialogCommissions((commissionsRes.data ?? []) as AffiliateCommissionRow[]);
    const taxRow = (taxRes.data ?? [])[0] as { status: string; expires_at: string | null } | undefined;
    setTaxStatus(taxRow ?? null);
  };

  const exportBatchCsv = () => {
    if (!dialogRow || dialogCommissions.length === 0) return;
    const rows: string[][] = [
      ["commission_id", "accrued_at", "referred_user_id", "gross_amount", "commission_amount", "currency", "stripe_invoice_id"],
      ...dialogCommissions.map((c) => [
        c.id,
        c.accrued_at,
        c.referred_user_id,
        (c.gross_amount_cents / 100).toFixed(2),
        (c.commission_amount_cents / 100).toFixed(2),
        c.currency.toUpperCase(),
        c.stripe_invoice_id ?? "",
      ]),
    ];
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(
      `affiliate-batch-${maskReferredUserId(dialogRow.affiliate_user_id)}-${dialogRow.currency}-${stamp}.csv`,
      rows,
    );
  };

  const submitBatch = async () => {
    if (!dialogRow || dialogCommissions.length === 0) return;
    setSubmitting(true);
    const { error } = await supabase.rpc("admin_create_payout_batch", {
      _affiliate_user_id: dialogRow.affiliate_user_id,
      _currency: dialogRow.currency,
      _payment_reference: paymentReference,
      _notes: notes,
      _commission_ids: dialogCommissions.map((c) => c.id),
      _allow_missing_tax: allowMissingTax,
    });
    setSubmitting(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Batch marked as paid");
    setDialogRow(null);
    setDialogCommissions([]);
    await Promise.all([loadPayable(), loadBatches()]);
  };

  return {
    payable, payableLoading, batches, batchesLoading,
    dialogRow, setDialogRow, dialogCommissions, dialogLoading,
    paymentReference, setPaymentReference,
    notes, setNotes,
    allowMissingTax, setAllowMissingTax,
    taxStatus, submitting,
    totalsByCurrency,
    connectByUser, running, env,
    runPayoutOne, runPayoutAll,
    exportPayableCsv, openBatchDialog, exportBatchCsv, submitBatch,
  };
}
